import { Controller } from "@hotwired/stimulus"
import { createGrid } from "ag-grid-community";
import { CustomDateTimeEditor } from './custom_date_time_editor';
import { CustomTimeEditor } from './custom_time_editor';
import { ForeignKeyEditor } from './foreign_key_editor';
import { ForeignKeyCellRenderer } from './foreign_key_cell_renderer';
import { fkDisplayFields } from './fk_display_cache';
import { SelectEditor } from '../ag_grid/cell_editors/select_editor';
import { spreadsheetTheme } from '../ag_grid/theme';

// Key the staged new row shares with the staged-edit machinery, so adds and updates save the same way.
const NEW_ROW_KEY = 'new';

// Stored actor codes and what they mean to a reader of the grid. 'A' is written when the change
// arrived over the REST API authenticated as an OAuth application.
const ACTOR_TYPE_LABELS = { U: 'User', A: 'API', I: 'Import' };

// Stored version states and what they mean to a reader of the grid. A delete writes a tombstone -
// a current version carrying trdm_is_deleted - so 'Deleted' comes off that flag rather than off a
// state of its own, and takes precedence over the stored 'C'.
const STATE_LABELS = { C: 'Current', H: 'History' };

// Expanding a record adds its history rows to every page, so cap how many can be open at
// once. Must match MAX_EXPANDED_RECORDS in Api::TablesController.
const MAX_EXPANDED_RECORDS = 25;

export default class extends Controller {
  static targets = ["container", "error", "deleteMessage"]
  static values = {
    tableDefinitionId: Number,
    columnDefinitions: Array,
    theme: String,
    loadType: String
  }

  // To store data for the new row in the pinned top row
  newRowData = {};
  requiredCols = [];
  isResettingPinnedRow = false;
  pendingDeleteTrdmSk = null; // Store trdm_sk for single row delete
  pendingDeleteTrdmSks = null; // Store trdm_sk array for multi-row delete  
  pendingWrites = new Set();
  dataRevision = 0;
  pendingGridRefresh = false;
  pendingGridPurge = false;
  scrollToNewRowPending = false;
  // Staged (unsaved) cell edits for existing rows, keyed by trdm_sk. Buffering them here is
  // what keeps a burst of cell edits down to a single new version per row.
  pendingEdits = new Map();
  savingRowKeys = new Set();
  focusedRowKey = null;
  // Show History reveals deleted records and puts a chevron on every row; the chevron then
  // pulls that record's earlier versions in below it. Both travel to the server, because the
  // infinite row model cannot have rows inserted into it client-side.
  showHistory = false;
  expandedRecordIds = new Set();
  restoreFirstRowIndex = null;

  connect() {
    // Initialize the grid
    this.newRowData = {}; // Ensure it's reset on reconnect if controller is reused
    this.pendingEdits = new Map();
    this.savingRowKeys = new Set();
    this.focusedRowKey = null;
    this.showHistory = false;
    this.expandedRecordIds = new Set();
    this.restoreFirstRowIndex = null;

    // Clicking anywhere outside the grid counts as the row losing focus.
    this.onOutsidePointerDown = (event) => {
      if (!this.element.contains(event.target)) this.flushPendingEdits();
    };
    this.onBeforeUnload = (event) => {
      if (!this.hasUnsavedEdits()) return;
      const message = this.unsavedChangesMessage();
      event.preventDefault();
      event.returnValue = message;
      return message;
    };
    this.onBeforeVisit = (event) => {
      if (!this.hasUnsavedEdits()) return;
      if (!window.confirm(this.unsavedChangesMessage())) event.preventDefault();
    };
    document.addEventListener('mousedown', this.onOutsidePointerDown, true);
    window.addEventListener('beforeunload', this.onBeforeUnload);
    document.addEventListener('turbo:before-visit', this.onBeforeVisit);

    this.initializeGrid();
  }

  disconnect() {
    document.removeEventListener('mousedown', this.onOutsidePointerDown, true);
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    document.removeEventListener('turbo:before-visit', this.onBeforeVisit);
  }

  initializeGrid() {
    // Determine and store required columns for new records once for performance.
    // A column is required if it's a primary key or explicitly marked as required,
    // but we exclude 'trdm_sk' as it's database-generated.
    this.requiredCols = this.columnDefinitionsValue.filter(
      cd => this.isBusinessColumn(cd) &&
      (cd.is_primary_key || cd.is_required)
    );
    const activeColumnDefinitions = this.columnDefinitionsValue.filter(colDef => this.isBusinessColumn(colDef));

    try {
      // Create grid options
      const gridOptions = {
        theme: spreadsheetTheme,
        // Enable multi-row selection with Ctrl/Shift-Click, but never on read-only rows
        rowSelection: {
          mode: 'multiRow',
          enableClickSelection: true,
          checkboxes: false,
          headerCheckbox: false,
          isRowSelectable: (node) => !this.isReadOnlyRow(node?.data)
        },
        columnDefs: this.getColumnDefs(),
        components: {
          customDateTimeEditor: CustomDateTimeEditor,
          customTimeEditor: CustomTimeEditor,
          foreignKeyEditor: ForeignKeyEditor,
          foreignKeyCellRenderer: ForeignKeyCellRenderer,
          selectEditor: SelectEditor
        },
        singleClickEdit: true,
        readOnlyEdit: true,
        onCellEditRequest: this.handleCellEditRequest.bind(this),
        defaultColDef: {
          filter: true,
          filterParams: {
            caseSensitive: false,
            buttons: ['reset']
          },
          editable: true, // Make columns editable by default for the pinned row
          wrapHeaderText: false, // Prevent header text wrapping when sort icon appears
          autoHeaderHeight: false,
          headerClass: 'ag-header-cell-text',
          valueFormatter: (params) =>
            this.isEmptyPinnedCell(params)
              ? this.createPinnedCellPlaceholder(params)
              : this.formatDisplayValue(params), // Use a general formatter for display
          suppressKeyboardEvent: (params) => {
            const isEditing = this.gridApi.getEditingCells().length > 0;
            if (params.event.key === 'Delete' && !isEditing) {
              this.deleteSelectedRows();
              return true; // Suppress the event
            }
            // Handle Esc key in pinned row to prevent unwanted save attempts
            if (params.event.key === 'Escape' && params.node && params.node.rowPinned === 'top') {
              // Set flag to prevent handlePinnedRowCellEdit from processing
              this.isResettingPinnedRow = true;
              setTimeout(() => {
                this.isResettingPinnedRow = false;
              }, 300);
            }
            return false; // Allow default behavior
          },
        },
        rowModelType: 'infinite',
        datasource: this.getDataSource(),
        cacheBlockSize: 100,
        maxBlocksInCache: 2,
        cacheOverflowSize: 1000,
        rowBuffer: 20,
        pagination: false,
        animateRows: true,
        suppressColumnVirtualisation: true, // Render all columns to calculate proper width
        suppressHorizontalScroll: false, // Allow horizontal scroll for non-pinned columns
        // enableCellEditingOnEscape: true, // Already commented out
        onCellEditingStopped: this.handlePinnedRowCellEdit.bind(this), // For new row in pinned row
        onCellFocused: this.handleCellFocused.bind(this), // Saves the row the caret just left
        pinnedTopRowData: this.loadTypeValue === 'manual' ? [this.newRowData] : [],
        getRowStyle: this.getRowStyle.bind(this),
        onGridReady: (params) => {
          console.log('Grid is ready');
          this.gridApi = params.api;
          this.columnApi = params.columnApi;
        },
        onFirstDataRendered: () => {
          // 1. Autosize all columns to fit their content.
          this.gridApi.autoSizeAllColumns();

          // 2. Add extra width to all columns to accommodate sort icons (20px)
          const allColumns = this.gridApi.getAllDisplayedColumns();
          const widthAdjustments = allColumns.map(column => ({
            key: column.getColId(),
            newWidth: column.getActualWidth() + 20 // Add 20px for sort icon space
          }));
          this.gridApi.setColumnWidths(widthAdjustments);

          // 3. Then, apply specific widths for columns with a display_length.
          const columnWidthUpdates = activeColumnDefinitions
            .filter(colDef => colDef.display_length != null)
            .map(colDef => ({
              key: colDef.field, // colId is set to field
              newWidth: colDef.display_length * 8, // Estimate width: 8px/char
            }));

          if (columnWidthUpdates.length > 0) {
            this.gridApi.setColumnWidths(columnWidthUpdates);
          }
          
          // 4. Calculate total column width and adjust container if needed
          setTimeout(() => {
            let totalWidth = 0;
            this.gridApi.getAllDisplayedColumns().forEach(column => {
              totalWidth += column.getActualWidth();
            });
            
            // Add a small buffer (20px) to account for borders/rounding, only if the vertical scrollbar is visible
            totalWidth += 20;
            
            // Get the current container width
            const containerWidth = this.containerTarget.offsetWidth;
            
            // Always set the container width to match the total column width
            // This prevents the scrollbar from appearing unnecessarily
            this.containerTarget.style.width = `${totalWidth}px`;
            this.containerTarget.style.minWidth = `${totalWidth}px`;
          }, 100);
        },

      }

      // Initialize the grid
      try {
        // Create the grid
        const grid = createGrid(this.containerTarget, gridOptions);

        // Store the grid instance
        this.grid = grid
      } catch (error) {
        console.error('Error initializing grid:', error)
        this.showError(`Error initializing grid: ${error.message}`)
      }
    } catch (error) {
      console.error('Error initializing grid:', error)
      this.showError(`Error initializing grid: ${error.message}`)
    }
  }

  // General value formatter for displaying data (can be expanded)
  formatDisplayValue(params) {
    // This is a basic placeholder. You might want to reuse your existing formatters
    // or call them conditionally based on params.colDef.cellRenderer etc.
    // For now, it just returns the value. If a specific valueFormatter is set on a colDef, it will take precedence.
    if (params.value == null) return ''; // Handle null/undefined consistently
    return params.value;
  }

  // Styling for the pinned row
  getRowStyle(params) {
    // Checked before the deleted branch: a deleted record's earlier versions still read as history.
    if (this.isHistoryRow(params.data)) {
      return { 'background-color': '#f3f4f6', 'color': '#6b7280' };
    }
    if (this.isDeletedRow(params.data)) {
      return { 'background-color': '#fff1f2', 'color': '#9f1239' };
    }
    if (params.node.rowPinned) {
      // Using Tailwind CSS utility classes by applying them directly if possible,
      // or returning style objects. For simplicity with getRowStyle, direct styles:
      return { 'font-weight': 'normal', 'font-style': 'italic', 'background-color': '#f0f8ff' }; // Example: AliceBlue background
    }
  }

  // Check if a cell in the pinned row is empty
  isEmptyPinnedCell(params) {
    // When the value formatter is used by the filter, params.node can be null.
    // We must check for its existence before accessing rowPinned.
    return (
      params.node &&
      params.node.rowPinned === 'top' &&
      (params.value == null || params.value === '')
    );
  }

  // Create placeholder text for empty cells in the pinned row
  createPinnedCellPlaceholder(params) {
    const field = params.colDef.field;
    if (!field || (field.startsWith('trdm_') && field !== 'trdm_record_id')) return '';
    // Attempt to get headerName if available, otherwise use field
    let displayName;
    if (field === 'trdm_record_id') {
      displayName = 'New Row';
    }
    else {
      displayName = params.colDef.headerName || field;
    }
    return displayName.charAt(0).toUpperCase() + displayName.slice(1) + '...';
  }

  // Handle cell editing stopped, specifically for the pinned row
  handlePinnedRowCellEdit(params) {
    if (params.rowPinned !== 'top') {
      return; // Not the pinned top row; existing rows stage their edits via handleCellEditRequest
    }

    // Skip processing if we're in the middle of resetting the pinned row
    if (this.isResettingPinnedRow) {
      return;
    }

    // AG Grid updates the object in pinnedTopRowData directly, so this.newRowData is already
    // up to date. Refresh the actions column so the add/discard icons pick up the new state.
    this.gridApi?.refreshCells({ force: true, columns: ['actions'] });
  }

  // Business-column values staged in the pinned top row.
  stagedNewRowChanges() {
    const changes = {};
    Object.keys(this.newRowData).forEach(field => {
      const columnDefinition = this.columnDefinitionsValue.find(cd => cd.field === field);
      if (this.isBusinessColumn(columnDefinition)) changes[field] = this.newRowData[field];
    });
    return changes;
  }

  hasStagedNewRow() {
    return Object.values(this.stagedNewRowChanges())
      .some(value => value !== null && value !== undefined && value !== '');
  }

  get isSavingNewRow() {
    return this.savingRowKeys.has(NEW_ROW_KEY);
  }

  // Check if all required fields in the newRowData are filled
  isNewRowDataComplete() {
    // Use the pre-calculated requiredCols array for efficiency.
    // This array is filtered based on is_primary_key and is_required flags from column definitions.
    if (!this.requiredCols) {
      console.error("Required columns definition is missing.");
      return false;
    }

    if (!this.hasStagedNewRow()) return false;

    return this.requiredCols.every(colDef => {
      const value = this.newRowData[colDef.field];
      // A value is considered "filled" if it's not null, undefined, or an empty string.
      return value !== null && value !== undefined && value !== '';
    });
  }

  // Throw away the staged new row without saving it.
  discardNewRow() {
    if (this.isSavingNewRow) return;
    this.resetPinnedRowData();
    this.gridApi?.refreshCells({ force: true, columns: ['actions'] });
  }

  // A new row only lands at the top of the grid while the default record id sort is in effect.
  newRowsLandAtTop() {
    const sorted = (this.gridApi?.getColumnState() || []).filter(state => state.sort);
    return sorted.length === 1 && sorted[0].colId === 'trdm_record_id' && sorted[0].sort === 'desc';
  }

  // Reset the pinned top row data
  resetPinnedRowData() {
    this.isResettingPinnedRow = true; // Set flag to prevent processing of triggered events
    this.newRowData = {}; // Clear the internal state
    // Use setGridOption as the modern and recommended way to update grid properties
    // Only set pinnedTopRowData if loadType is 'manual'
    const pinnedData = this.loadTypeValue === 'manual' ? [this.newRowData] : [];
    this.gridApi.setGridOption('pinnedTopRowData', pinnedData);
    // Reset the flag after a longer delay to allow AG Grid events to complete
    // Esc key events may take longer to process than Tab events
    setTimeout(() => {
      this.isResettingPinnedRow = false;
    }, 200);
  }

  isBusinessColumn(colDef) {
    return !!colDef?.field && !colDef.is_deleted &&
      !colDef.field.startsWith('trdm_') && !colDef.database_name?.startsWith('trdm_');
  }

  // A closed-off earlier version of a record.
  isHistoryRow(data) {
    return data?.trdm_state === 'H';
  }

  // The current version of a record that has been deleted: the row survives as a tombstone.
  isDeletedRow(data) {
    return data?.trdm_is_deleted === true;
  }

  isReadOnlyRow(data) {
    return this.isHistoryRow(data) || this.isDeletedRow(data);
  }

  // Read-only rows get no key, which is what keeps them out of the staged-edit machinery:
  // pendingEdits, applyPendingEdits, nodesByRowKey and handleCellFocused all key off this.
  // Focusing one therefore looks like focus leaving the grid, so a staged new row is not
  // committed by clicking a history row — it fails safe rather than adding silently.
  rowKey(data) {
    if (this.isReadOnlyRow(data)) return null;
    return data?.trdm_sk != null ? String(data.trdm_sk) : null;
  }

  hasPendingEdits(data) {
    const key = this.rowKey(data);
    return !!key && this.pendingEdits.has(key);
  }

  hasUnsavedEdits() {
    return this.pendingEdits.size > 0 || this.hasStagedNewRow();
  }

  unsavedChangesMessage() {
    const parts = [];
    if (this.pendingEdits.size) {
      parts.push(`unsaved changes to ${this.pendingEdits.size} row${this.pendingEdits.size === 1 ? '' : 's'}`);
    }
    if (this.hasStagedNewRow()) parts.push('a new row that has not been added yet');
    return `You have ${parts.join(' and ')}. Leave the page and lose that work?`;
  }

  // Resolve a staged row key to the entry saveRows sends. The pinned new row is only ever
  // offered once its required fields are filled, so a half-typed row is never submitted.
  stagedEntry(key) {
    if (key !== NEW_ROW_KEY) return this.pendingEdits.get(key) || null;
    if (!this.isNewRowDataComplete()) return null;
    return { key: NEW_ROW_KEY, trdmSk: null, changes: this.stagedNewRowChanges() };
  }

  // Re-apply staged edits to rows coming back from the server, so unsaved work survives a
  // cache refresh or a block being evicted and re-fetched by the infinite row model.
  applyPendingEdits(row) {
    const entry = this.pendingEdits.get(this.rowKey(row));
    if (!entry) return row;
    // entry.display carries the reference display fields belonging to the pending values;
    // without them a refreshed row would show the server's old name next to the new id.
    return { ...row, ...entry.changes, ...(entry.display || {}) };
  }

  nodesByRowKey() {
    const nodes = new Map();
    this.gridApi?.forEachNode(node => {
      const key = node.rowPinned ? null : this.rowKey(node.data);
      if (key) nodes.set(key, node);
    });
    return nodes;
  }

  rowWriteKeys(data) {
    return [
      data?.trdm_sk != null ? `version:${data.trdm_sk}` : null,
      data?.trdm_record_id != null ? `record:${data.trdm_record_id}` : null
    ].filter(Boolean);
  }

  isRowWritePending(data) {
    const keys = this.rowWriteKeys(data);
    return [...this.pendingWrites].some(write => keys.some(key => write.keys.has(key)));
  }

  canEditRow(params) {
    if (params.node?.rowPinned === 'top') return !this.isSavingNewRow;
    if (this.isReadOnlyRow(params.data)) return false;
    return params.data?.trdm_sk != null && !this.pendingGridPurge && !this.isRowWritePending(params.data);
  }

  beginRowWrite(rows) {
    if (rows.length && this.pendingGridPurge) {
      this.showError('The grid is refreshing current data. Wait before editing or deleting rows.');
      return null;
    }
    if (rows.some(row => this.isRowWritePending(row))) {
      this.showError('This row has a change in progress. Wait for it to finish before editing or deleting it.');
      return null;
    }
    const write = { keys: new Set(rows.flatMap(row => this.rowWriteKeys(row))) };
    write.done = new Promise(resolve => { write.resolve = resolve; });
    this.pendingWrites.add(write);
    this.dataRevision++;
    this.gridApi?.refreshCells({ force: true, columns: ['actions'] });
    return write;
  }

  finishRowWrite(write) {
    this.pendingWrites.delete(write);
    this.dataRevision++;
    write.resolve();
    this.gridApi?.refreshCells({ force: true, columns: ['actions'] });
    this.flushGridRefresh();
  }

  refreshGridData(purge = false) {
    this.dataRevision++;
    this.pendingGridRefresh = true;
    this.pendingGridPurge ||= purge;
    this.flushGridRefresh();
  }

  flushGridRefresh() {
    if (!this.pendingGridRefresh || this.pendingWrites.size) return;
    const purge = this.pendingGridPurge;
    this.pendingGridRefresh = false;
    this.pendingGridPurge = false;
    if (purge) {
      this.gridApi.purgeInfiniteCache();
    } else {
      this.gridApi.refreshInfiniteCache();
    }
  }

  // Show History toggle above the grid: reveals deleted records and the per-row chevrons.
  toggleShowHistory(event) {
    this.showHistory = !!event.target.checked;
    if (!this.showHistory) this.expandedRecordIds.clear();
    // Staged edits would otherwise sit through a purge with no row on screen to save them.
    this.flushPendingEdits();
    this.rememberScrollPosition();
    // Deleted rows appear or disappear, so the row count changes and the cache must be purged.
    this.refreshGridData(true);
  }

  // Chevron in the Uid column: pull this record's earlier versions in below it, or drop them.
  toggleRecordHistory(recordId) {
    if (recordId == null) return;
    if (this.expandedRecordIds.has(recordId)) {
      this.expandedRecordIds.delete(recordId);
    } else if (this.expandedRecordIds.size >= MAX_EXPANDED_RECORDS) {
      this.showError(`History is open for ${MAX_EXPANDED_RECORDS} rows. Collapse one before expanding another.`);
      return;
    } else {
      this.expandedRecordIds.add(recordId);
    }
    this.rememberScrollPosition();
    this.refreshGridData(true);
  }

  // Expanding or collapsing only adds or removes rows below the row that was clicked, so
  // holding the first displayed index keeps the grid visually still across the purge.
  rememberScrollPosition() {
    const firstIndex = this.gridApi?.getFirstDisplayedRowIndex?.();
    this.restoreFirstRowIndex = firstIndex >= 0 ? firstIndex : null;
  }

  handleVersionConflict() {
    this.showError('This row changed elsewhere. The grid is being refreshed with current data. Review it before trying again.');
    this.refreshGridData(true);
  }

  handleCellEditRequest(event) {
    const colDef = this.columnDefinitionsValue.find(cd => cd.field === event.colDef.field);
    if (!this.isBusinessColumn(colDef)) return;
    if (event.node?.rowPinned === 'top') {
      if (this.isSavingNewRow || event.data !== this.newRowData) return;
      this.newRowData[event.colDef.field] = event.newValue;
      if (colDef.foreign_key_reference_details) {
        Object.assign(this.newRowData, fkDisplayFields(colDef, event.newValue) || {});
      }
      this.gridApi.refreshCells({ rowNodes: [event.node], force: true });
      return;
    }
    this.stageCellEdit(event, colDef);
  }

  // Buffer an edit against the row rather than writing it straight to the backend. The row is
  // only sent when it loses focus or the user clicks the tick, so several cell edits collapse
  // into one revision.
  stageCellEdit(event, columnDefinition) {
    const { node, data, newValue, oldValue } = event;
    const key = this.rowKey(data);
    if (!key || !node || node.data !== data || this.gridApi.getRowNode(node.id) !== node) return;
    if (!this.canEditRow(event)) return;
    if (this.valuesAreEquivalent(columnDefinition, oldValue, newValue)) return;

    const field = columnDefinition.field;
    const entry = this.pendingEdits.get(key) || { key, trdmSk: data.trdm_sk, changes: {}, original: {} };
    if (!(field in entry.original)) entry.original[field] = oldValue;

    // Editing a cell back to the value it started with clears the change rather than saving a no-op.
    if (this.valuesAreEquivalent(columnDefinition, entry.original[field], newValue)) {
      delete entry.changes[field];
      delete entry.original[field];
      if (entry.display) {
        Object.keys(entry.display)
          .filter(name => name.startsWith(`${field}_`))
          .forEach(name => delete entry.display[name]);
      }
    } else {
      entry.changes[field] = newValue;
    }

    if (Object.keys(entry.changes).length) {
      this.pendingEdits.set(key, entry);
    } else {
      this.pendingEdits.delete(key);
    }

    // A reference also owns the joined display fields the cell renders, so they move with
    // the value. Doing it here rather than in the editor is deliberate: this always has the
    // live row node, whereas an open editor's params.data can be replaced out from under it
    // by a grid refresh, leaving the new id beside the previous name.
    const display = columnDefinition.foreign_key_reference_details
      ? fkDisplayFields(columnDefinition, newValue)
      : null;
    if (display) entry.display = { ...(entry.display || {}), ...display };

    node.setData({ ...data, [field]: newValue, ...(display || {}) });
    this.gridApi.refreshCells({ force: true, columns: ['actions'] });
  }

  valuesAreEquivalent(columnDefinition, oldValue, newValue) {
    if (columnDefinition?.data_type === 'date' && oldValue instanceof Date && newValue instanceof Date) {
      return formatDateOnly(oldValue) === formatDateOnly(newValue);
    }
    // Treat null, undefined and '' as the same "empty" so clearing an empty cell is not a change.
    const oldIsEmpty = oldValue === null || oldValue === undefined || oldValue === '';
    const newIsEmpty = newValue === null || newValue === undefined || newValue === '';
    if (oldIsEmpty || newIsEmpty) return oldIsEmpty && newIsEmpty;
    return oldValue === newValue;
  }

  serializeValue(columnDefinition, value) {
    if (columnDefinition?.data_type === 'date' && value instanceof Date) return formatDateOnly(value);
    return value;
  }

  serializeChanges(entry) {
    const attributes = {};
    Object.keys(entry.changes).forEach(field => {
      const columnDefinition = this.columnDefinitionsValue.find(cd => cd.field === field);
      if (!this.isBusinessColumn(columnDefinition)) return;
      // Use database_name for backend communication (handles dangerous column names)
      attributes[columnDefinition.database_name || field] = this.serializeValue(columnDefinition, entry.changes[field]);
    });
    return attributes;
  }

  // Moving the caret to a different row saves the row we just left.
  handleCellFocused(params) {
    let key = null;
    if (params.rowPinned === 'top') {
      key = NEW_ROW_KEY;
    } else if (!params.rowPinned && params.rowIndex != null) {
      key = this.rowKey(this.gridApi?.getDisplayedRowAtIndex(params.rowIndex)?.data);
    }
    if (key === this.focusedRowKey) return;

    const previousKey = this.focusedRowKey;
    this.focusedRowKey = key;
    if (!previousKey) return;

    // Only add the staged new row when the caret lands on another row in the grid. Focus simply
    // leaving the grid must not commit an add the user may have been about to abandon.
    if (previousKey === NEW_ROW_KEY) {
      if (key) this.saveRows([NEW_ROW_KEY]);
      return;
    }
    if (this.pendingEdits.has(previousKey)) this.saveRows([previousKey]);
  }

  // Commit any open editor, then save every row that still has staged changes.
  flushPendingEdits() {
    if (this.gridApi?.getEditingCells()?.length) this.gridApi.stopEditing(false);
    if (!this.pendingEdits.size) return;
    this.saveRows([...this.pendingEdits.keys()]);
  }

  // Send staged rows to the batch endpoint. Each row becomes exactly one new version,
  // regardless of how many of its cells were edited.
  async saveRows(keys) {
    const entries = keys
      .filter(key => !this.savingRowKeys.has(key))
      .map(key => this.stagedEntry(key))
      .filter(Boolean);
    if (!entries.length) return;

    // Only existing rows contribute write keys; a create has no row in the grid to guard.
    const nodes = this.nodesByRowKey();
    const rows = entries.flatMap(entry => (
      entry.trdmSk == null ? [] : [nodes.get(entry.key)?.data || { trdm_sk: entry.trdmSk }]
    ));
    const write = this.beginRowWrite(rows);
    if (!write) return;

    entries.forEach(entry => this.savingRowKeys.add(entry.key));
    this.gridApi?.refreshCells({ force: true, columns: ['actions'] });
    let created = false;

    // A row with no trdm_sk is a create; the batch endpoint revises or creates accordingly,
    // and either way the row costs exactly one new version.
    const payloadRows = entries.map(entry => {
      const attributes = this.serializeChanges(entry);
      return entry.trdmSk == null ? { attributes } : { trdm_sk: entry.trdmSk, attributes };
    });

    try {
      const csrfToken = document.querySelector("meta[name='csrf-token']")?.getAttribute('content');
      const response = await fetch(`/api/tables/${this.tableDefinitionIdValue}/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        },
        body: JSON.stringify({ batch: { rows: payloadRows } })
      });

      if (response.status === 409) {
        entries.forEach(entry => this.pendingEdits.delete(entry.key));
        this.handleVersionConflict();
        return;
      }

      const responseData = await response.json();
      if (!response.ok) {
        throw new Error(`${responseData.error || 'Unknown error'}${responseData.errors ? `. ${responseData.errors.join(', ')}` : ''}`);
      }

      const records = responseData.records || [];
      if (records.length !== entries.length) {
        throw new Error('The saved rows could not be verified. Refreshing current data.');
      }

      const currentNodes = this.nodesByRowKey();
      entries.forEach((entry, index) => {
        if (entry.key === NEW_ROW_KEY) {
          this.resetPinnedRowData();
          this.scrollToNewRowPending = this.newRowsLandAtTop();
          created = true;
          return;
        }
        this.pendingEdits.delete(entry.key);
        const node = currentNodes.get(entry.key);
        // The saved row is a new version, so swap in the returned record (new trdm_sk,
        // refreshed audit columns and foreign key display names).
        if (node) node.setData(this.transformRecordToFieldNames(records[index]));
      });
      this.showSuccess(`${entries.length} row(s) saved successfully.`);
    } catch (error) {
      console.error('Error saving row changes:', error);
      // Staged edits, including the pinned new row, are kept so the user can correct and retry.
      this.showError(`Error saving changes: ${error.message}`);
    } finally {
      entries.forEach(entry => this.savingRowKeys.delete(entry.key));
      // A create changes the row count and where the new row sorts, so the cache must be
      // purged. So does saving while history is expanded, since the edit closes off the
      // previous version and that becomes another history row.
      this.refreshGridData(created || this.expandedRecordIds.size > 0);
      this.finishRowWrite(write);
    }
  }

  showError(message) {
    if (!this.hasErrorTarget) return;
    this.errorTarget.querySelector('p').textContent = message;
    this.errorTarget.classList.remove('hidden');
    clearTimeout(this._errorTimer);
    this._errorTimer = setTimeout(() => {
      this.errorTarget.classList.add('hidden');
      this.errorTarget.querySelector('p').textContent = '';
    }, 15000);
  }

  showSuccess(message) {
    console.log('Success:', message);
  }

  // Transform record from database_name keys to field keys for AG Grid display
  transformRecordToFieldNames(record) {
    const transformed = {};
    
    // Build a map of database_name -> field for quick lookup
    const dbNameToField = {};
    this.columnDefinitionsValue.forEach(colDef => {
      if (!this.isBusinessColumn(colDef) || !colDef.database_name) return;
      // A reference has no scalar column of its own - it is stored as
      // <name>_trdm_record_id / <name>_trdm_sk - so the referenced record id is the cell's
      // value. Without this the cell has no value at all, which leaves oldValue undefined
      // and stops the grid from detecting (and re-rendering) an edit.
      const recordIdName = colDef.foreign_key_reference_details?.record_id_column_name;
      dbNameToField[recordIdName || colDef.database_name] = colDef.field;
    });

    // Transform each key in the record
    Object.keys(record).forEach(key => {
      const fieldName = dbNameToField[key] || key; // Use field name if mapped, otherwise keep original
      transformed[fieldName] = record[key];
    });

    return transformed;
  }

  getDataSource() {
    return {
      getRows: async (params) => {
        console.log('Fetching rows:', params)
        try {
          while (true) {
            while (this.pendingWrites.size) {
              await Promise.all([...this.pendingWrites].map(write => write.done));
            }
            const revision = this.dataRevision;
            const response = await fetch(`/api/tables/${this.tableDefinitionIdValue}/grid`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                startRow: params.startRow,
                endRow: params.endRow,
                filterModel: params.filterModel,
                sortModel: params.sortModel,
                showHistory: this.showHistory,
                expandedRecordIds: [...this.expandedRecordIds]
              })
            });
            if (revision !== this.dataRevision || this.pendingWrites.size) continue;
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`)
            }
            console.log('Response status:', response.status)
            const data = await response.json();
            if (revision !== this.dataRevision || this.pendingWrites.size) continue;
            console.log('Data received:', data)
            // Transform all rows from database_name keys to field keys
            const transformedRows = data.rows.map(row => this.applyPendingEdits(this.transformRecordToFieldNames(row)));
            params.successCallback(transformedRows, data.rowCount)
            if (this.scrollToNewRowPending && data.rowCount > 0) {
              this.scrollToNewRowPending = false;
              this.restoreFirstRowIndex = null;
              this.gridApi.ensureIndexVisible(0, 'top');
            } else if (this.restoreFirstRowIndex != null && data.rowCount > 0) {
              // Put the viewport back where the history toggle left it.
              const target = Math.min(this.restoreFirstRowIndex, data.rowCount - 1);
              this.restoreFirstRowIndex = null;
              this.gridApi.ensureIndexVisible(target, 'top');
            }
            return;
          }
        } catch (error) {
          console.error('Error fetching data:', error)
          this.showError(`Error loading data: ${error.message}`)
          this.showError(`Error loading data params: ${params}`)
          params.failCallback();
        }
      }
    }
  }

  // Uid cell: the record id, preceded by the history chevron while Show History is on.
  renderUidCell(params) {
    const wrap = document.createElement('div');
    wrap.className = 'h-full w-full flex items-center gap-1';

    // A cellRenderer replaces the formatter's output, so re-emit the "New Row..." placeholder.
    if (params.node?.rowPinned === 'top') {
      wrap.textContent = params.valueFormatted ?? '';
      return wrap;
    }

    // History rows belong to the row above, which already shows the Uid, so this cell stays
    // empty. The muted row styling and the expanded chevron above carry the relationship.
    if (this.isHistoryRow(params.data)) {
      wrap.title = 'Earlier version of the row above';
      return wrap;
    }

    const recordId = params.data?.trdm_record_id;
    if (this.showHistory && recordId != null) {
      const expanded = this.expandedRecordIds.has(recordId);
      const chevron = document.createElement('button');
      chevron.type = 'button';
      chevron.dataset.action = 'toggle-history';
      chevron.className = 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200';
      chevron.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      // Deliberately offered on deleted rows too: their earlier versions are the main
      // reason to turn Show History on.
      chevron.title = expanded ? 'Hide earlier versions' : 'Show earlier versions';
      chevron.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}">
          <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
        </svg>`;

      // mousedown, like every other button here: a click would also select the row.
      chevron.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleRecordHistory(recordId);
      });

      wrap.appendChild(chevron);
    }

    const label = document.createElement('span');
    label.textContent = params.valueFormatted ?? (params.value ?? '');
    wrap.appendChild(label);
    return wrap;
  }

  getColumnDefs() {
    const activeColumnDefinitions = this.columnDefinitionsValue.filter(colDef => this.isBusinessColumn(colDef));

    // Create the actions column with add/delete icons
    const actionsColDef = {
      headerName: '',
      colId: 'actions',
      width: 76,
      minWidth: 68,
      maxWidth: 90,
      sortable: false,
      filter: false,
      suppressMenu: true,
      editable: false,
      suppressNavigable: true,
      pinned: 'left',
      lockPosition: true,
      suppressMovable: true,
      cellRenderer: (params) => {
        const wrap = document.createElement('div');
        wrap.className = 'h-full w-full flex items-center justify-center gap-1';
        
        // Pinned top row: show add (+) and discard (x) icons only if loadType is 'manual'
        if (params.node?.rowPinned === 'top' && this.loadTypeValue === 'manual') {
          const canAdd = !this.isSavingNewRow && this.isNewRowDataComplete();
          const hasStagedRow = this.hasStagedNewRow();

          const add = document.createElement('button');
          add.type = 'button';
          add.dataset.action = 'create';
          // Deliberately not `disabled`: a disabled button swallows mousedown, so a click here
          // while the first cell is still being edited would strand the open editor.
          add.setAttribute('aria-disabled', canAdd ? 'false' : 'true');
          add.title = canAdd ? 'Add new row' : 'Fill required fields to add row';
          add.className = canAdd ? 'text-green-600 hover:text-green-700' : 'text-gray-400';
          add.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" class="w-5 h-5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>`;

          // Use mousedown instead of click to intercept before AG Grid processes the event
          add.addEventListener('mousedown', (e) => {
            e.preventDefault();
            e.stopPropagation();

            // Commit an in-progress editor first, then add whatever the pinned row has staged.
            if (this.gridApi?.getEditingCells()?.length) this.gridApi.stopEditing(false);
            setTimeout(() => this.saveRows([NEW_ROW_KEY]), 0);
          });

          wrap.appendChild(add);

          const discard = document.createElement('button');
          discard.type = 'button';
          discard.dataset.action = 'discard';
          discard.setAttribute('aria-disabled', hasStagedRow ? 'false' : 'true');
          discard.title = hasStagedRow ? 'Discard this new row' : 'Nothing to discard';
          discard.className = hasStagedRow
            ? 'text-red-600 hover:text-red-700'
            : 'text-gray-300 dark:text-gray-600';
          discard.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" class="w-5 h-5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>`;

          discard.addEventListener('mousedown', (e) => {
            e.preventDefault();
            e.stopPropagation();

            // Cancel any open editor so its value is not written back after we clear the row.
            if (this.gridApi?.getEditingCells()?.length) this.gridApi.stopEditing(true);
            this.discardNewRow();
          });

          wrap.appendChild(discard);
          return wrap;
        }

        // If pinned top row but loadType is not 'manual', return empty wrapper
        if (params.node?.rowPinned === 'top') {
          return wrap;
        }

        // History rows cannot be edited, and a deleted record cannot be deleted again.
        if (this.isReadOnlyRow(params.node?.data)) {
          return wrap;
        }

        // Regular rows: show save (tick) and delete (-) icons
        const hasChanges = this.hasPendingEdits(params.node?.data);
        const canSave = hasChanges && this.canEditRow(params);

        const save = document.createElement('button');
        save.type = 'button';
        save.dataset.action = 'save';
        // Deliberately not `disabled`: a disabled button swallows mousedown, which would strand an
        // open editor whose first change has not been committed yet.
        save.setAttribute('aria-disabled', canSave ? 'false' : 'true');
        save.className = canSave ? 'text-green-600 hover:text-green-700' : 'text-gray-300 dark:text-gray-600';
        save.title = canSave ? 'Save changes to this row' : 'No unsaved changes';
        save.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" class="w-5 h-5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>`;

        save.addEventListener('mousedown', (e) => {
          e.preventDefault();
          e.stopPropagation();

          // Commit an in-progress editor first, then save whatever the row has staged.
          if (this.gridApi?.getEditingCells()?.length) this.gridApi.stopEditing(false);
          setTimeout(() => {
            const key = this.rowKey(this.gridApi?.getRowNode(params.node?.id)?.data);
            if (key) this.saveRows([key]);
          }, 0);
        });

        wrap.appendChild(save);

        const del = document.createElement('button');
        del.type = 'button';
        del.dataset.action = 'delete';
        del.className = 'text-red-600 hover:text-red-700';
        del.title = 'Delete row';
        del.disabled = !this.canEditRow(params);
        del.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" class="w-5 h-5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>`;
        
        // Use mousedown for delete as well for consistency
        del.addEventListener('mousedown', (e) => {
          e.preventDefault();
          e.stopPropagation();
          
          const trdmSk = params.node?.data?.trdm_sk;
          if (!trdmSk) {
            this.showError('Cannot delete row: Missing identifier');
            return;
          }
          
          this.showDeleteModal(1, trdmSk);
        });
        
        wrap.appendChild(del);
        return wrap;
      }
    };

    // Map database types to AG Grid cell data types
    const typeMap = {
      // Text types
      'varchar': 'text',
      'character': 'text',
      'text': 'text',
      // Integer types
      'integer': 'number',
      'smallint': 'number',
      'bigint': 'number',
      // Decimal types
      'decimal': 'number',
      'real': 'number',
      'double': 'number',
      // Boolean
      'boolean': 'boolean',
      // Date/Time types
      'timestamp': 'text',        
      'timestamptz': 'text',      
      'time': 'text',           
      'timetz': 'text',         
      'date': 'date',
      // Binary
      'binary': 'text' 
    };

    // Helper function to create number formatter
    const createNumberFormatter = (scale) => {
      return (params) => {
        if (params.value === null || params.value === undefined) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: scale,
          maximumFractionDigits: scale
        }).format(params.value);
      };
    };

    // Helper function to create number parser
    const createNumberParser = (scale) => {
      return (params) => {
        if (params.newValue === null || params.newValue === undefined || params.newValue === '') return null;
        return parseFloat(params.newValue);
      };
    };

  // Create the Uid column definition, sourced from the stable record identifier
  const uidColDef = {
    field: 'trdm_record_id',
    colId: 'trdm_record_id',
    headerName: 'Uid',
    // Default sort: the record id is stable across revisions, so a saved row keeps its place
    sort: 'desc',
    sortIndex: 0,
    headerTooltip: 'Stable identifier for the record across all of its versions',
    cellDataType: 'number',
    editable: false,
    sortable: true,
    filter: 'agNumberColumnFilter',
    resizable: true,
    suppressNavigable: true,
    cellRenderer: (params) => this.renderUidCell(params)
  };

  // Create user column definitions
  // Sort column definitions by display_order
  const sortedColumnDefs = activeColumnDefinitions.sort((a, b) => {
    // Handle cases where display_order might be null or undefined
    const orderA = a.display_order ?? Infinity;
    const orderB = b.display_order ?? Infinity;
    return orderA - orderB;
  });

  const userColumnDefs = sortedColumnDefs.map(colDef => {
      const baseDef = {
        colId: colDef.field, // Pass the ColumnDefinition ID
        field: colDef.field,
        headerName: colDef.header_name || colDef.field,
        filter: colDef.filter !== false,
        sortable: colDef.sortable !== false,
        resizable: colDef.resizable !== false,
        // Disable and prevent navigation for imported columns
        suppressNavigable: colDef.is_imported,
        editable: params => !colDef.is_imported && this.canEditRow(params),
        headerTooltip: colDef.description || '',
        suppressKeyboardEvent: colDef.is_imported ? (params) => true : undefined
      };

      // Set cellDataType if we have a mapping for this data type
      if (colDef.data_type && typeMap[colDef.data_type]) {
        baseDef.cellDataType = typeMap[colDef.data_type];
      }

      // Add appropriate editor and formatter for timestamp columns
      if (['timestamp', 'timestamptz'].includes(colDef.data_type)) {
        baseDef.cellEditor = 'customDateTimeEditor';
        baseDef.valueFormatter = dateTimeValueFormatter; // Apply the new formatter
        baseDef.valueParser = params => {
        // CustomDateTimeEditor returns an ISO string or null.
        // AG Grid with cellDataType: 'date' expects a Date object or a string it can parse.
        if (params.newValue) {
          const date = new Date(params.newValue);
          const isValidDate = !isNaN(date.getTime());
          const returnValue = isValidDate ? date : null;
          return returnValue;
        }
        return null;
      } 
      } 
      
      // Add appropriate editor for boolean columns
      if (colDef.data_type === 'boolean') {
        // Use built-in checkbox editor; AG Grid v33 does not include agBooleanCellEditor
        baseDef.cellEditor = 'agCheckboxCellEditor';
      } 
      
      // Add appropriate editor for date columns
      if (colDef.data_type === 'date') {
        baseDef.cellEditor = 'agDateCellEditor';
        baseDef.valueFormatter = dateValueFormatter; // For display
        baseDef.valueGetter = dateStringParserValueGetter; // For editor initialization
      }

      // Add appropriate editor for text columns
      if (colDef.data_type === 'text') {
        baseDef.cellEditor = 'agLargeTextCellEditor';
        baseDef.cellEditorPopup = true;
        baseDef.cellEditorParams = {
          rows: 4,
          cols: 100
        }
      }

      // Add appropriate editor and formatter for time columns
      if (['time', 'timetz'].includes(colDef.data_type)) {
        baseDef.cellEditor = 'customTimeEditor';
        baseDef.valueFormatter = timeValueFormatter; // Apply the new time formatter
      }  

      // Configure ForeignKeyEditor for foreign key columns
      // Ensure colDef.is_foreign_key and colDef.foreign_key_reference_details are provided from backend
      const hasForeignKeyDetails = !!colDef.foreign_key_reference_details?.foreign_table_id;
      const isForeignKeyColumn = !!(colDef.foreign_key_table_id || colDef.is_foreign_key || hasForeignKeyDetails);
      if (isForeignKeyColumn && hasForeignKeyDetails) {
        baseDef.cellEditor = 'foreignKeyEditor';
        baseDef.cellEditorParams = {  
          foreignKeyReferenceDetails: colDef.foreign_key_reference_details,
        };
        baseDef.cellRenderer = 'foreignKeyCellRenderer';
        baseDef.cellRendererParams = {
          pkIsLookupColumn: !!colDef.foreign_key_reference_details?.pk_is_lookup_column,
          // Names the server aliases the referenced key columns under, as
          // `<col>_<pkName>_ref` on each row.
          pkColumnNames: colDef.foreign_key_reference_details?.primary_key_column_names || [],
        };
        //baseDef.cellDataType = 'text';
        baseDef.editable = params => this.canEditRow(params);
      }

      // Add number formatting for decimal types
      if (['decimal', 'real', 'double'].includes(colDef.data_type)) {
        // Use scale from column definition if available, otherwise default to 2 decimal places

        const scale = colDef.scale || 2;
        baseDef.cellEditor = 'agNumberCellEditor';
        baseDef.cellEditorParams = {
          precision: scale,
          showStepperButtons: false
        }
        baseDef.valueFormatter = createNumberFormatter(scale);
        baseDef.valueParser = createNumberParser(scale);
      }

      // Check for valid_values last, as this editor takes precedence if defined
      if (colDef.valid_values && typeof colDef.valid_values === 'string' && colDef.valid_values.trim() !== '') {
        let validValuesArray = colDef.valid_values.split(',').map(val => val.trim());
        if (!colDef.is_required) {
          validValuesArray.unshift(""); // Add a blank option for non-required fields
        }
        if (validValuesArray.length > 0) {
          baseDef.cellEditor = 'selectEditor';
          baseDef.cellEditorParams = {
            values: validValuesArray
          };
        }
      }

      return baseDef;
    }); // End of creating ColDef

    // Create audit columns (read-only, displayed at the end)
    const auditColumnDefs = [
      // Each change writes a new version, so the current version's creation time is the moment
      // the record was last updated.
      { field: 'trdm_created_at', headerName: 'Updated At', filter: 'agDateColumnFilter', valueFormatter: dateTimeValueFormatter, headerTooltip: 'When this version of the record was written — that is, when the record was last updated, or for a deleted record, when it was deleted' },
      { field: 'trdm_actor_type', headerName: 'Actor Type', valueFormatter: actorTypeValueFormatter, headerTooltip: 'Who made the change: User (U), API (A) — an OAuth application calling the REST API — or Import (I, reserved). Filter using the letter.' },
      { field: 'trdm_actor_id', headerName: 'Actor ID', cellDataType: 'number', filter: 'agNumberColumnFilter' },
      { field: 'trdm_sk', headerName: 'Version Id', cellDataType: 'number', filter: 'agNumberColumnFilter', headerTooltip: 'Identifier of this specific version of the record' },
      { field: 'trdm_state', headerName: 'State', valueFormatter: stateValueFormatter, headerTooltip: 'Current (C) is the latest version of the record, History (H) a version that was superseded. Deleted is a current version that records the record having been deleted - Updated At is when that happened. Filter using the letter.' },
      { field: 'trdm_effective_from', headerName: 'Effective From', filter: 'agDateColumnFilter', valueFormatter: dateTimeValueFormatter },
      { field: 'trdm_effective_to', headerName: 'Effective To', filter: 'agDateColumnFilter', valueFormatter: dateTimeValueFormatter }
    ].map(colDef => ({
      filter: true,
      ...colDef,
      colId: colDef.field,
      editable: false,
      sortable: true,
      resizable: true,
      suppressNavigable: true,
      cellClass: 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
    }));

    // Combine actions column, Uid column definition, user column definitions, and audit columns
    const columnDefs = [actionsColDef, uidColDef, ...userColumnDefs, ...auditColumnDefs];

    console.log('Column definitions:', columnDefs)
    return columnDefs
  }



  async deleteSelectedRows() {
    const selectedNodes = this.gridApi.getSelectedNodes();
    const selectedIds = selectedNodes
      .filter(node => !node.rowPinned && node.data?.trdm_sk != null && !this.isReadOnlyRow(node.data))
      .map(node => node.data.trdm_sk);

    if (selectedIds.length === 0) {
      this.showError('No rows selected to delete.');
      return;
    }

    this.showDeleteModal(selectedIds.length, null, selectedIds);
  }

  rowsForDelete(ids) {
    const rows = new Map();
    this.gridApi.forEachNode(node => {
      // Read-only rows are never eligible, so a stale history or tombstone id fails closed
      // down the "row changed or is no longer loaded" path below.
      if (!node.rowPinned && node.data?.trdm_sk != null && !this.isReadOnlyRow(node.data)) {
        rows.set(String(node.data.trdm_sk), node.data);
      }
    });
    const selectedRows = ids.map(id => rows.get(String(id)));
    if (selectedRows.some(row => !row)) {
      this.showError('A selected row changed or is no longer loaded. Review the refreshed grid and select it again.');
      this.refreshGridData(true);
      return null;
    }
    if (this.pendingGridPurge || selectedRows.some(row => this.isRowWritePending(row))) {
      this.showError('A selected row has a change in progress. Wait for it to finish before deleting.');
      return null;
    }
    return selectedRows;
  }

  async performDeleteSelectedRows() {
    const selectedIds = this.pendingDeleteTrdmSks;
    if (!selectedIds || selectedIds.length === 0) return;
    const rows = this.rowsForDelete(selectedIds);
    if (!rows) return;
    const write = this.beginRowWrite(rows);
    if (!write) return;

    const csrfToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content");
    const url = `/api/tables/${this.tableDefinitionIdValue}/delete_records`;

    try {
      const response = await fetch(url, {
        method: 'POST', // Using POST to send a body with the IDs
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({ trdm_sk_values: selectedIds }),
      });

      if (response.status === 409) {
        this.handleVersionConflict();
        return;
      }
      const responseData = await response.json();
      if (!response.ok) {
        throw new Error(responseData.error || 'Failed to delete records.');
      }

      selectedIds.forEach(id => this.pendingEdits.delete(String(id)));
      this.showSuccess(`${responseData.deleted_count ?? selectedIds.length} row(s) deleted successfully.`);
      this.gridApi.deselectAll(); // Clear selection
      this.refreshGridData(true); // Refresh the grid data
    } catch (error) {
      console.error('Error deleting rows:', error);
      this.showError(`Error deleting rows: ${error.message}`);
      this.refreshGridData(true);
    } finally {
      this.finishRowWrite(write);
    }
  }

  // Show delete confirmation modal
  showDeleteModal(count, trdmSk = null, trdmSks = null) {
    if (!this.rowsForDelete(trdmSks || [trdmSk])) return;
    // Store the pending delete info
    this.pendingDeleteTrdmSk = trdmSk;
    this.pendingDeleteTrdmSks = trdmSks;
    
    // Update the modal message
    if (this.hasDeleteMessageTarget) {
      if (count === 1) {
        this.deleteMessageTarget.textContent = 'Are you sure you want to delete this row?';
      } else {
        this.deleteMessageTarget.textContent = `Are you sure you want to delete ${count} rows?`;
      }
    }
    
    // Show the modal
    const modal = document.getElementById('delete-rows-modal');
    if (modal) {
      modal.classList.remove('hidden');
    }
  }

  // Confirm delete action (called from modal button)
  confirmDelete() {
    // Hide the modal
    const modal = document.getElementById('delete-rows-modal');
    modal.classList.add('hidden');
    
    // Perform the appropriate delete
    if (this.pendingDeleteTrdmSk) {
      this.deleteRow(this.pendingDeleteTrdmSk);
      this.pendingDeleteTrdmSk = null;
    } else if (this.pendingDeleteTrdmSks) {
      this.performDeleteSelectedRows();
      this.pendingDeleteTrdmSks = null;
    }
  }

  // Delete a single row
  async deleteRow(trdmSk) {
    const rows = this.rowsForDelete([trdmSk]);
    if (!rows) return;
    const write = this.beginRowWrite(rows);
    if (!write) return;
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content");
    const url = `/api/tables/${this.tableDefinitionIdValue}/delete_records`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({ trdm_sk_values: [trdmSk] }),
      });

      if (response.status === 409) {
        this.handleVersionConflict();
        return;
      }
      const responseData = await response.json();
      if (!response.ok) {
        throw new Error(responseData.error || 'Failed to delete record.');
      }

      this.pendingEdits.delete(String(trdmSk));
      this.showSuccess(`${responseData.deleted_count ?? 1} row(s) deleted successfully.`);
      this.refreshGridData(true); // Refresh the grid data
    } catch (error) {
      console.error('Error deleting row:', error);
      this.showError(`Error deleting row: ${error.message}`);
      this.refreshGridData(true);
    } finally {
      this.finishRowWrite(write);
    }
  }
}

// Show the stored actor code as the kind of actor it stands for
function actorTypeValueFormatter(params) {
  if (!params.value) return '';
  return ACTOR_TYPE_LABELS[params.value] || params.value;
}

// Show the stored state code as the kind of version it stands for, with a deleted record's
// tombstone reading as 'Deleted' rather than as the 'Current' version it technically is.
function stateValueFormatter(params) {
  if (params.data?.trdm_is_deleted === true) return 'Deleted';
  if (!params.value) return '';
  return STATE_LABELS[params.value] || params.value;
}

// Format a Date as YYYY-MM-DD using local date parts (never UTC, which can shift the day)
function formatDateOnly(date) {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Custom Value Getter to parse YYYY-MM-DD string to Date object for editor
function dateStringParserValueGetter(params) {
  const field = params.colDef.field;
  if (!params.data || typeof params.data[field] === 'undefined' || params.data[field] === null) {
    return null; // Return null if data or field is not present
  }
  const value = params.data[field];

  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    // Parse YYYY-MM-DD string. Split and construct to ensure local date interpretation.
    // new Date('YYYY-MM-DD') can be tricky with timezones (often treats as UTC midnight).
    // Constructing with Date(year, monthIndex, day) uses local time.
    const parts = value.split('-');
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // JavaScript months are 0-indexed
    const day = parseInt(parts[2], 10);
    if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
      return new Date(year, month, day);
    }
    console.warn('dateStringParserValueGetter: Could not parse date string parts:', value);
    return null; // Or handle as an invalid date string
  }
  // If it's already a Date object, or some other type, return as is (or handle error)
  if (value instanceof Date) {
    return value;
  }
  console.warn('dateStringParserValueGetter: Value is not a YYYY-MM-DD string or Date object:', value);
  return null; // Fallback for unexpected types/formats that are not empty
}

// Custom Value Formatter for Date (YYYY-MM-DD)
function dateValueFormatter(params) {
  if (!params.value) {
    return ''; // Return empty string for null or undefined values
  }

  // Check if the value is already a string in YYYY-MM-DD format
  if (typeof params.value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(params.value)) {
    // It's in the correct format, return as is.
    // This assumes the backend sends dates in 'YYYY-MM-DD' format.
    return params.value;
  }

  // If it's a Date object, format it to YYYY-MM-DD
  if (params.value instanceof Date) {
    const date = params.value;
    if (isNaN(date.getTime())) {
      console.warn('dateValueFormatter: Invalid Date object received:', params.value);
      return ''; // Or handle as appropriate
    }
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Fallback for other types or unexpected values that are not empty
  console.warn('dateValueFormatter: Unexpected value type/format received:', params.value, typeof params.value);
  return params.value.toString(); // Attempt to convert to string, or return '' if preferred for unknowns
}

// Custom Value Formatter for Date-Time with Milliseconds
function dateTimeValueFormatter(params) {
  if (!params.value) {
    return ''; // Return empty string for null or undefined values
  }

  const date = new Date(params.value);

  if (isNaN(date.getTime())) {
    // If the value is not a valid date string, return it as is or show an error/placeholder
    // This helps identify problematic data from the backend
    console.warn('dateTimeValueFormatter: Invalid date value received:', params.value);
    return params.value; // Or return 'Invalid Date', or ''
  }

  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');
  const milliseconds = date.getMilliseconds().toString()

  const millisecondsInt = parseInt(milliseconds || '0')
  // Only show milliseconds if they are not zero, or always show them based on preference
  // For now, let's always show them if the column is timestamp/timestamptz
  if (millisecondsInt !== 0) {
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds}`;
  }
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

// Custom Value Formatter for Time with Milliseconds
function timeValueFormatter(params) {
  if (!params.value) {
    return ''; // Return empty string for null or undefined values
  }

  let valueToParse = params.value;
  // If it's a string and doesn't look like a full date string, prepend a dummy date.
  // This helps new Date() parse time-only strings like "HH:MM:SS.mmm"
  // It also helps correctly interpret time strings with timezone offsets if present.
  if (typeof valueToParse === 'string' && !valueToParse.includes('T') && !valueToParse.includes('-') && valueToParse.includes(':')) {
    valueToParse = `1970-01-01T${valueToParse}`; 
  }

  const date = new Date(valueToParse);

  if (isNaN(date.getTime())) {
    console.warn('timeValueFormatter: Invalid time value received:', params.value);
    return params.value; // Or return 'Invalid Time'
  }

  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');
  const milliseconds = date.getMilliseconds();

  if (milliseconds > 0) {
    const paddedMs = milliseconds.toString().padStart(3, '0');
    return `${hours}:${minutes}:${seconds}.${paddedMs}`;
  } else {
    return `${hours}:${minutes}:${seconds}`;
  }
}
